import { GetSettings } from '@/services/api/settings';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery } from 'react-query';

// Debounce utility function
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const NotificationPanel = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const {data: settings, isLoading} = useQuery(['settings'], GetSettings)
  const slideCount = settings?.promotion_texts?.length;
  const reversedPromotionTexts = [...(settings?.promotion_texts ?? [])].reverse();

  const slideTo = (index: number) => {
    const container = scrollRef.current;
    if (container) {
      const slideWidth = container.clientWidth;
      const scrollAmount = index * slideWidth;
      container.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Debounced scroll handler
  const handleScroll = useCallback(
    debounce(() => {
      const container = scrollRef.current;
      if (container) {
        const scrollLeft = container.scrollLeft;
        const slideWidth = container.clientWidth;
        const index = Math.round(scrollLeft / slideWidth);
        
        if (index !== currentSlide) {
          setCurrentSlide(index);
        }
      }
    }, 100),
    [currentSlide]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const nextSlide = (currentSlide + 1) % slideCount;
      slideTo(nextSlide);
      setCurrentSlide(nextSlide);
    }, 5000);

    return () => clearInterval(interval);
  }, [currentSlide, slideCount]);

  return (
    <>
      {(settings?.promotion_texts?.length > 0 && settings?.promotion_texts !== undefined) ? (
        <div className="bg-yellow-500 py-[16px] px-1 min-w-full">
          <div className="flex flex-col items-center">
            {/* Scrollable promotion texts - LTR scrolling but RTL text */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory hide-scrollbar w-full"
              dir="ltr" // LTR for scrolling behavior
            >
              {reversedPromotionTexts !== undefined && reversedPromotionTexts?.map((item: any, index: number) => (
                <div
                  key={index}
                  className="min-w-full flex items-center justify-center snap-start text-center md:text-left px-2 text-[13px] mb-2"
                  dir="rtl" // RTL for text display only
                >
                  <p className="text-primary text-[0.65rem] leading-[.9]">
                    {item?.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Navigation Dots - Correct order for LTR slider */}
            <div className="flex flex-row-reverse gap-1 pt-1">
              {[...Array(slideCount)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => slideTo(index)}
                  className={`w-4 h-1 rounded-full transition ${
                    index === currentSlide ? 'bg-primary' : 'bg-[#E6E9ED]'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      ): (
        null
      )}
    </>
  );
};

export default NotificationPanel;